import { useEffect, useState } from 'react';
import { IonButton, IonCheckbox } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { Text } from '../components/Text';
import { AnimatedCollapse } from '../components/AnimatedCollapse';
import { ITaskStep } from './IOnBoardingTask';
import { ChevronButton } from '../components/ChevronButton';
import { PlayIcon } from '../theme/Icons';

const useStyles = createUseStyles(() => ({
    wrapper: {
        padding: '20px 15px',
        borderRadius: 10,
        border: '1px solid var(--ion-color-step-100)'
    },
    checkbox: {
        pointerEvents: 'none',
        cursor: 'default',
        '&::part(mark)': {
            strokeWidth: 3
        }
    },

    videoPlaceholder: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer'
    },

    playIcon: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1
    },

    videoMask: {
        position: 'absolute',
        width: 200,
        height: '100%',
        background: 'rgb(0,0,0,0.5)'
    }
}));

export interface ITaskStepSectionProps {
    step: ITaskStep;
    expanded: boolean;
};

const StepButton: React.FC<{
    label: string;
    action: () => void;
    disabled?: boolean;
    dataType?: string;
}> = ({
    label,
    action,
    disabled,
    dataType
}) => {
    return (
        <div style={{marginTop: 15}}>
            <IonButton
                data-type={dataType}
                disabled={disabled}
                style={{minWidth: 120}}
                onClick={action}
            >{label}</IonButton>
        </div>
    );
};

export const TaskStepSection: React.FC<ITaskStepSectionProps> = ({ step, expanded }) => {
    const classes = useStyles();

    const mixpanel = useInstance(MixPanelEventEmitter);

    const [isOpen, setOpen] = useState(expanded || false);
    const [currentVideo, setCurrentVideo] = useState<{ id: string; path: string } | undefined>();

    useEffect(() => setOpen(expanded), [expanded]);

    const onToggle = () => {
        mixpanel.track('Onboarding Item Expansion Toggled', () => ({
            Name: step.heading,
            State: (isOpen ? 'closed' : 'open')
        }));

        setOpen(!isOpen);
    };

    const onStepTodoAction = () => {
        mixpanel.track('Onboarding Item Opened', () => ({
            Name: step.heading,
            Button: step.todoBtn.label
        }));

        step.todoBtn.action();
    };

    const onStepDoneAction = () => {
        mixpanel.track('Completed Onboarding Item Revisited', () => ({
            Name: step.heading,
            Button: step.todoBtn.label
        }));

        step.doneBtn?.action();
    };

    const playVideo = (id: string, path: string) => {
        setCurrentVideo({ id, path });
    };

    const disabled = !step.ready && !step.completed;

    return (
        <div data-type="onboarding-step" data-expanded={String(isOpen)} className={classes.wrapper}>

            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer'}} onClick={onToggle}>

                <div style={{display: 'flex', alignItems: 'center', gap: 10 }}>
                    <IonCheckbox className={classes.checkbox} mode="ios" color="success" checked={step.completed} onClick={() => false}/>
                    <Text data-type="heading" disabled={disabled}>
                        <b style={{color: step.completed ? 'var(--ion-color-success)' : undefined}}>{step.heading}</b>
                    </Text>
                </div>

                <ChevronButton direction={isOpen ? 'up' : 'down'} />
            </div>

            <AnimatedCollapse collapsed={!isOpen} durationMs={250}>
                <div style={{marginTop: 20}}>
                    {step.image && (
                        <center><img src={step.image()} alt={`${step.heading}`} /></center>
                    )}
                    {step.video && (
                        <center>
                            {currentVideo && currentVideo.id === step.video.id ? (
                                <div style={{marginBottom: 16}}>
                                    <video src={currentVideo.path} autoPlay width="200" controls />
                                </div>
                            ) : (
                                <div
                                    style={{marginBottom: 16}}
                                    className={classes.videoPlaceholder}
                                    onClick={() => playVideo(step.video?.id!, step.video?.movie!)}
                                >
                                    <div className={classes.playIcon}>
                                        {/* use customSize to match size of ios play button */}
                                        <PlayIcon color="light" customSize={42} />
                                    </div>
                                    <div className={classes.videoMask}></div>
                                    <img src={step.video.thumbnail} alt={`${step.heading}`} width="200" />
                                </div>
                            )}
                        </center>
                    )}
                    <Text type="detail" disabled={disabled}>{step.text}</Text>
                </div>

                {!step.completed ? (
                    typeof step.todoBtn.label === 'string' ? (
                        <StepButton
                            dataType="onboarding-action"
                            disabled={disabled}
                            label={step.todoBtn.label as string}
                            action={onStepTodoAction}
                        />
                    ) : (
                        step.todoBtn.label
                    )
                ) : typeof step.doneBtn?.label === 'string' ? (
                    <StepButton
                        dataType="done-onboarding-action"
                        label={step.doneBtn?.label as string}
                        action={onStepDoneAction}
                    />
                ) : (
                    step.doneBtn?.label
                )}
            </AnimatedCollapse>
        </div>
    );
};
